/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { getPublicAssetUrl } from '../../utils/publicAsset';
import isMobileDevice from '../../utils/isMobileDevice';
import MobileDownloadPromptModal from './MobileDownloadPromptModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HeaderItem {
  label: string;
  href: string;
  menu?: React.ReactNode;
}

export interface HeaderProps {
  /** Nav items rendered in the centre of the header */
  items: HeaderItem[];
  /** When true the dropdown opens on hover instead of click */
  autoOpenMenu?: boolean;
  /** Optional logo element – falls back to the RF logo. Pass a function to receive isDark. */
  logo?: React.ReactNode | ((isDark: boolean) => React.ReactNode);
  /** Optional trailing element (buttons, avatars, etc.) */
  actions?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export function Header({ items, autoOpenMenu = false, logo, actions }: HeaderProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenuIndex, setMobileSubmenuIndex] = useState<number | null>(null);
  const [showMobileDownloadPrompt, setShowMobileDownloadPrompt] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const lastScrollTop = useRef(0);

  // Hide header on scroll down, show on scroll up (mobile only via CSS)
  useEffect(() => {
    const scrollContainer = headerRef.current?.parentElement;
    if (!scrollContainer) return undefined;

    const handleScrollDirection = () => {
      const scrollTop = scrollContainer.scrollTop;
      if (scrollTop - lastScrollTop.current > 10) {
        setIsHidden(true);
      } else if (lastScrollTop.current - scrollTop > 10) {
        setIsHidden(false);
      }
      lastScrollTop.current = scrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScrollDirection);
  }, []);

  // Detect when header overlaps a dark section
  useEffect(() => {
    const check = () => {
      if (!headerRef.current) return;
      const headerBottom = headerRef.current.getBoundingClientRect().bottom;
      const darkSections = document.querySelectorAll('[data-header-theme="dark"]');
      let overDark = false;
      darkSections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (
          headerBottom >= rect.top &&
          headerRef.current!.getBoundingClientRect().top <= rect.bottom
        ) {
          overDark = true;
        }
      });
      setIsDark(overDark);
    };

    // The scroll container is the main element wrapping everything
    const scrollContainer = headerRef.current?.parentElement;
    const target = scrollContainer || window;

    const handleScroll = () => requestAnimationFrame(check);
    target.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Check immediately and also after a short delay to catch lazy-loaded content
    check();
    const t1 = setTimeout(check, 100);
    const t2 = setTimeout(check, 500);

    // Also observe DOM changes so we catch dynamically rendered dark sections
    const observer = new MutationObserver(() => requestAnimationFrame(check));
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      target.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenIndex(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change / escape
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        setOpenIndex(null);
      }
    }
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const openMenu = useCallback((idx: number) => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setOpenIndex(idx);
  }, []);

  const closeMenu = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpenIndex(null), 150);
  }, []);

  const toggleMenu = useCallback(
    (idx: number) => setOpenIndex((prev) => (prev === idx ? null : idx)),
    []
  );

  const handleGetStartedClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMobileDevice()) {
      return;
    }
    event.preventDefault();
    setMobileOpen(false);
    setShowMobileDownloadPrompt(true);
  }, []);

  return (
    <header
      ref={headerRef}
      className={cn(
        'sticky top-0 z-50 w-full transition-all duration-300',
        isDark ? 'bg-gray-950' : 'bg-white',
        isHidden && !mobileOpen && '-translate-y-full'
      )}
    >
      <div
        ref={navRef}
        className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-[clamp(1rem,2vw,2rem)]"
      >
        {/* Logo */}
        <Link to="/" className="shrink-0">
          {(typeof logo === 'function' ? logo(isDark) : logo) ?? (
            <div className="relative h-14">
              {/* Desktop logos */}
              <img
                className="hidden lg:block h-14 absolute inset-0 transition-opacity duration-300"
                style={{ opacity: isDark ? 0 : 1 }}
                src={getPublicAssetUrl('/assets/images/home/RF-Logo-Blue.svg')}
                alt="RealityFence Logo"
              />
              <img
                className="hidden lg:block h-14 absolute inset-0 transition-opacity duration-300"
                style={{ opacity: isDark ? 1 : 0 }}
                src={getPublicAssetUrl('/assets/images/home/RF-Logo-White.svg')}
                alt="RealityFence Logo"
              />
              {/* Mobile logos */}
              <img
                className="block lg:hidden h-14 absolute inset-0 transition-opacity duration-300"
                style={{ opacity: isDark ? 0 : 1 }}
                src={getPublicAssetUrl('/assets/images/home/rf_logo_blue_mobile_optimized.png')}
                alt="RealityFence Logo"
              />
              <img
                className="block lg:hidden h-14 absolute inset-0 transition-opacity duration-300"
                style={{ opacity: isDark ? 1 : 0 }}
                src={getPublicAssetUrl('/assets/images/home/rf_logo_white_mobile_optimized.png')}
                alt="RealityFence Logo"
              />
              {/* Invisible spacer to maintain width */}
              <img
                className="hidden lg:block h-14 invisible"
                src={getPublicAssetUrl('/assets/images/home/RF-Logo-Blue.svg')}
                alt=""
                aria-hidden="true"
              />
              <img
                className="block lg:hidden h-14 invisible"
                src={getPublicAssetUrl('/assets/images/home/rf_logo_blue_mobile_optimized.png')}
                alt=""
                aria-hidden="true"
              />
            </div>
          )}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {items.map((item, idx) => (
            <div
              key={item.label}
              className="relative"
              {...(autoOpenMenu
                ? {
                    onMouseEnter: () => item.menu && openMenu(idx),
                    onMouseLeave: () => item.menu && closeMenu(),
                  }
                : {})}
            >
              {item.menu ? (
                <button
                  type="button"
                  onClick={() => toggleMenu(idx)}
                  className={cn(
                    'flex items-center gap-1 rounded-lg px-3 py-2 text-[15px] font-medium transition-colors',
                    isDark
                      ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                    openIndex === idx &&
                      (isDark ? 'bg-white/10 text-white' : 'bg-gray-50 text-gray-900')
                  )}
                >
                  {item.label}
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-200',
                      isDark ? 'text-gray-500' : 'text-gray-400',
                      openIndex === idx && 'rotate-180'
                    )}
                  />
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    'rounded-lg px-3 py-2 text-[15px] font-medium transition-colors',
                    isDark
                      ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  {item.label}
                </Link>
              )}

              {/* Dropdown */}
              {item.menu && openIndex === idx && (
                <div
                  className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2"
                  onMouseEnter={() => autoOpenMenu && openMenu(idx)}
                  onMouseLeave={() => autoOpenMenu && closeMenu()}
                >
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                    {item.menu}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden lg:flex items-center gap-3">
          {actions ?? (
            <>
              <Link
                to="/auth/login"
                className="rounded-full px-4 py-2 text-[15px] font-semibold text-gray-600 transition-opacity hover:opacity-80"
              >
                Login
              </Link>
              <Link
                to="/start"
                onClick={handleGetStartedClick}
                className="rounded-lg bg-gray-950 px-5 py-2.5 text-[15px] font-bold text-white transition-opacity hover:opacity-90"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className={cn(
            'lg:hidden flex items-center justify-center rounded-lg p-2',
            isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-50'
          )}
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <HamburgerIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 animate-in fade-in duration-200 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />

          {/* Drawer */}
          <div className="absolute inset-y-0 right-0 w-full max-w-sm animate-in slide-in-from-right duration-200 bg-white shadow-xl">
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
              <Link to="/" onClick={() => setMobileOpen(false)}>
                <img
                  className="h-10"
                  src={getPublicAssetUrl('/assets/images/home/rf_logo_blue_mobile_optimized.png')}
                  alt="RealityFence Logo"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-50"
                aria-label="Close menu"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>

            <nav className="overflow-y-auto px-4 py-4" style={{ maxHeight: 'calc(100vh - 140px)' }}>
              {items.map((item, idx) => (
                <div key={item.label} className="border-b border-gray-100 last:border-0">
                  {item.menu ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setMobileSubmenuIndex((prev) => (prev === idx ? null : idx))}
                        className="flex w-full items-center justify-between py-3 text-base font-medium text-gray-700"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-gray-400 transition-transform duration-200',
                            mobileSubmenuIndex === idx && 'rotate-180'
                          )}
                        />
                      </button>
                      {mobileSubmenuIndex === idx && (
                        <div className="pb-3" onClick={() => setMobileOpen(false)}>
                          {item.menu}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-base font-medium text-gray-700"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile actions */}
              <div className="mt-6 flex flex-col gap-3">
                <Link
                  to="/auth/login"
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-full border border-[#1FA9FF] py-2.5 text-center text-base font-semibold text-[#1FA9FF]"
                >
                  Login
                </Link>
                <Link
                  to="/start"
                  onClick={handleGetStartedClick}
                  className="block rounded-lg bg-[#1FA9FF] py-2.5 text-center text-base font-bold text-white"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
      <MobileDownloadPromptModal
        isOpen={showMobileDownloadPrompt}
        onClose={() => setShowMobileDownloadPrompt(false)}
      />
    </header>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons (avoids extra deps)
// ---------------------------------------------------------------------------

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function HamburgerIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default Header;
